import React, { useMemo } from 'react'
import type { WorkflowNodeData } from '@/types/workflow'
import TagAutocomplete from '@/components/ui/TagAutocomplete'
import MultiSelectCheckbox from '@/components/ui/MultiSelectCheckbox'
import CollapsibleSection from '@/components/ui/CollapsibleSection'
import { SENIORITY_OPTIONS } from '@/lib/seniority'

interface TriggerConfigProps {
  data: WorkflowNodeData
  updateField: (field: string, value: any) => void
}

import { cn } from '@/lib/utils'
import {
  inputClass,
  inputStyle,
  sectionClass,
  infoBannerClass,
  bannerTextStyle,
} from './_kit'
import { Select } from '@/components/ui/Select'
import { Field } from '@/components/ui/Field'

const TRIGGER_TYPE_OPTIONS = [
  { value: 'manual', label: 'Manual Start' },
  { value: 'contact_added', label: 'Contact Added to List' },
]

const LEAD_STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'engaged', label: 'Engaged' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'unqualified', label: 'Unqualified' },
]

// Labels show the employee-count range each bucket covers (kept in sync with
// AddContactModal.tsx and backend/app/core/company_size.py's bucket_for_count).
const COMPANY_SIZE_OPTIONS = [
  { value: 'startup', label: '1-9' },
  { value: 'small', label: '10-50' },
  { value: 'medium', label: '51-200' },
  { value: 'large', label: '201-1000' },
  { value: 'enterprise', label: '1000+' },
]

const TriggerConfig: React.FC<TriggerConfigProps> = ({ data, updateField }) => {
  const filterCriteria = data.filter_criteria || {}

  const updateFilter = (key: string, value: any) => {
    updateField('filter_criteria', { ...filterCriteria, [key]: value })
  }

  const activeFilterCount = useMemo(() => {
    let count = 0
    if (filterCriteria.tags?.length) count++
    if (filterCriteria.lead_status?.length) count++
    if (filterCriteria.seniority_levels?.length) count++
    if (filterCriteria.industries?.length) count++
    if (filterCriteria.company_sizes?.length) count++
    return count
  }, [filterCriteria])

  const isContactAdded = data.trigger_type === 'contact_added'

  // Legacy trigger types (e.g. 'schedule', 'webhook') were removed from the
  // options; show them as Manual Start rather than an empty select.
  const triggerTypeValue = TRIGGER_TYPE_OPTIONS.some(o => o.value === data.trigger_type)
    ? (data.trigger_type as string)
    : 'manual'

  return (
    <div>
      {/* Trigger Type + Label — always visible */}
      <div className="space-y-3 mb-5">
        <Field label="Trigger Type">
          {(f) => (
            <Select
              {...f}
              value={triggerTypeValue}
              onChange={(value) => updateField('trigger_type', value)}
              options={TRIGGER_TYPE_OPTIONS}
              className="w-full"
            />
          )}
        </Field>

        <Field label="Label">
          {(f) => (
            <input
              {...f}
              type="text"
              value={data.label || ''}
              onChange={(e) => updateField('label', e.target.value)}
              placeholder="e.g., When contact is added..."
              className={inputClass}
              style={inputStyle}
            />
          )}
        </Field>
      </div>

      {/* Contact Added to List trigger: lists are now chosen at launch time,
          not here, see Launch Campaign's Audience step. */}
      {isContactAdded && (
        <div className={cn(infoBannerClass, 'mb-5')} style={bannerTextStyle}>
          Which list(s) this workflow watches is chosen when you launch a campaign for it, not here.
          Every campaign you launch from this workflow will watch the list(s) you pick at launch time,
          and re-run this workflow automatically whenever a contact is added to one of them.
        </div>
      )}

      {/* Filter Criteria */}
      <CollapsibleSection
        title="Filter Criteria"
        defaultOpen={activeFilterCount > 0}
        badge={activeFilterCount > 0 ? `${activeFilterCount} active` : undefined}
      >
        <div className={sectionClass}>
          {/* Tags */}
          <Field label="Tags" as="group">
            <TagAutocomplete
              selectedTags={filterCriteria.tags || []}
              onChange={(tags) => updateFilter('tags', tags)}
            />
          </Field>

          {/* Lead Status */}
          <Field label="Lead Status" as="group">
            <MultiSelectCheckbox
              options={LEAD_STATUS_OPTIONS}
              selected={filterCriteria.lead_status || []}
              onChange={(values) => updateFilter('lead_status', values)}
              placeholder="Any status"
              colorClass="bg-[#5e6ad2]/15 text-[#5e6ad2] dark:text-[#a5b4fc]"
            />
          </Field>

          {/* Seniority */}
          <Field label="Seniority" as="group">
            <MultiSelectCheckbox
              options={SENIORITY_OPTIONS}
              selected={filterCriteria.seniority_levels || []}
              onChange={(values) => updateFilter('seniority_levels', values)}
              placeholder="Any seniority"
              colorClass="bg-[#5e6ad2]/15 text-[#5e6ad2] dark:text-[#a5b4fc]"
            />
          </Field>

          {/* Company Size */}
          <Field label="Company Size" as="group">
            <MultiSelectCheckbox
              options={COMPANY_SIZE_OPTIONS}
              selected={filterCriteria.company_sizes || []}
              onChange={(values) => updateFilter('company_sizes', values)}
              placeholder="Any size"
              colorClass="bg-[#5e6ad2]/15 text-[#5e6ad2] dark:text-[#a5b4fc]"
            />
          </Field>

          {/* Industry */}
          <Field label="Industry">
            {(f) => (
              <input
                {...f}
                type="text"
                value={(filterCriteria.industries || [])[0] || ''}
                onChange={(e) => updateFilter('industries', e.target.value ? [e.target.value] : [])}
                placeholder="e.g., Technology"
                className={inputClass}
                style={inputStyle}
              />
            )}
          </Field>
        </div>
      </CollapsibleSection>
    </div>
  )
}

export default TriggerConfig
